"use client"

import { Loader2 } from "lucide-react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { useTenantNotificationPoliciesQuery } from "@/hooks/queries/tenant-notification-policies"
import { BILLING_LOW_BALANCE_EVENT_TYPE, type TenantNotificationRecipients } from "@/lib/notifications/events"
import type { TenantNotificationView } from "@/server-actions/tenant-notification-policies"
import { LowBalanceThresholdField } from "./low-balance-threshold-field"
import { NotificationGroup } from "./notification-group"
import { TenantNotificationRow } from "./tenant-notification-row"
import { useTranslations } from "next-intl"


/** In display order; `satisfies` makes every recipient kind need a group. */
function getRECIPIENT_GROUPS(tUi: ReturnType<typeof useTranslations<"SystemNotifications.SystemNotificationsTenantNotificationsTab">>) {
  return {
  account_alerts: {
    title: tUi("accountAlerts"),
    route:
      "Sent to the channels each tenant picks under Account alerts on its Notification Channels page; " +
      "when none of them can deliver, to the tenant owner's email.",
  },
  agent_settings: {
    title: tUi("agentAlerts"),
    route: "Sent to the channels chosen in each agent's alert settings; an agent with none chosen sends nothing.",
  },
  store_owner: {
    title: tUi("shopifyStoreOwner"),
    route: "Emailed to the owner of the tenant's connected Shopify store.",
  },
  own_card: {
    title: tUi("theirOwnCard"),
    route:
      "Sent to the channels each tenant picks on the notification's own card on its Notification Channels page; " +
      "until it picks, where its Account alerts go.",
  },
} satisfies Record<TenantNotificationRecipients, { title: string; route: string }>
}

/** Every notification tenants receive, grouped by who gets it, and whether the platform sends it at all. */
export function TenantNotificationsTab() {
  const tUi = useTranslations("SystemNotifications.SystemNotificationsTenantNotificationsTab")
  const RECIPIENT_GROUPS = getRECIPIENT_GROUPS(tUi)


  const { data, isLoading, isError } = useTenantNotificationPoliciesQuery()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-10">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (isError || !data) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          <span key={tUi("couldNotLoadTenantNotifications")}>{tUi("couldNotLoadTenantNotifications")}</span></AlertDescription>
      </Alert>
    )
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        <span key={tUi("whatTenantsHearAboutTheirOwnAccountAnd")}>{tUi("whatTenantsHearAboutTheirOwnAccountAnd")}</span></p>
      {(Object.keys(RECIPIENT_GROUPS) as TenantNotificationRecipients[]).map((recipients) => {
        const notifications = data.filter((notification) => notification.recipients === recipients)
        if (notifications.length === 0) return null
        const group = RECIPIENT_GROUPS[recipients]

        return (
          <NotificationGroup key={recipients} id={`tenant-group-${recipients}`} title={group.title} description={group.route}>
            {notifications.map((notification) => (
              <TenantNotificationRow
                key={notification.eventType}
                notification={notification}
                extra={extraSetting(notification)}
              />
            ))}
          </NotificationGroup>
        )
      })}
    </div>
  )
}

function extraSetting({ eventType }: TenantNotificationView) {
  return eventType === BILLING_LOW_BALANCE_EVENT_TYPE ? <LowBalanceThresholdField /> : undefined
}
